import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useLocation } from "wouter";
import { Check, AlertCircle, Eye, EyeOff, ArrowLeft, Plus } from "lucide-react";
import { supabase, type Existencia, type Zona } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { useAuth } from "@/auth/AuthProvider";
import { rpc, CLAVES_DOMINIO } from "@/lib/useRpc";
import { mapearErrorSupabase, registrarError } from "@/lib/errores";
import { cantidad, num, hace, signo } from "@/lib/formato";
import { Tarjeta, Boton, Esqueleto, Vacio, Badge, Input, Dialogo, ErrorBloque } from "@/components/ui";
import { BuscadorSku } from "@/features/movimientos/BuscadorSku";
import { useToast } from "@/components/ui/Toast";

const LIMITE_LISTA = 300;

type EstadoFila = { valor: string; estado: "idle" | "guardando" | "ok" | "error"; error?: string; anterior?: number; nuevo?: number };

export function Contar() {
  const { zonaId } = useParams<{ zonaId?: string }>();
  const { almacen } = useAlmacen();
  if (!almacen) return <Esqueleto />;
  if (!zonaId) return <ElegirZona almacenId={almacen.id} />;
  return <ContarZona key={`${almacen.id}-${zonaId}`} almacenId={almacen.id} zonaId={zonaId} />;
}

function ElegirZona({ almacenId }: { almacenId: string }) {
  const zonas = useQuery({
    queryKey: ["zonas", "resumen", almacenId],
    queryFn: async () => {
      const { data, error } = await supabase.from("v_zonas_ultimo_conteo").select("*").eq("almacen_id", almacenId).order("codigo");
      if (error) throw error;
      return data;
    },
  });
  if (zonas.isLoading) return <Esqueleto filas={6} />;
  if (zonas.error) return <ErrorBloque mensaje={mapearErrorSupabase(zonas.error).mensaje} reintentar={() => zonas.refetch()} />;
  const conProductos = (zonas.data ?? []).filter((z) => (z.n_existencias ?? 0) > 0);
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <h1 className="text-2xl font-bold">Contar</h1>
      <p className="text-tinta-suave">Elige una zona. Cuenta lo que hay en el estante, sin ver lo que “debería” haber.</p>
      {conProductos.length === 0 ? (
        <Tarjeta><Vacio titulo="Este almacén no tiene productos con zona" texto="Importa tu catálogo o da de alta productos; las zonas se crean solas a partir de la ubicación (A-01-03 → zona A)." accion={<Link href="/importar"><Boton>Importar</Boton></Link>} /></Tarjeta>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {conProductos.map((z) => {
            const d = z.dias_sin_conteo;
            return (
              <li key={z.zona_id ?? ""}>
                <Link href={`/contar/${z.zona_id}`} className="flex h-full items-center justify-between gap-3 rounded-md border border-borde bg-white p-4 hover:border-primary">
                  <span>
                    <span className="flex items-center gap-2"><span className="flex h-9 w-9 items-center justify-center rounded bg-sidebar text-lg font-bold text-white">{(z.codigo ?? "?").slice(0, 3)}</span><span className="font-semibold">{z.nombre}</span></span>
                    <span className="mt-1 block text-sm text-tinta-suave">{z.n_existencias} SKUs · {z.n_contadas_7d} contados en 7 días</span>
                  </span>
                  <Badge tono={d == null ? "neutro" : d === 0 ? "ok" : d > 7 ? "error" : "alerta"}>{d == null ? "nunca" : d === 0 ? "hoy" : `hace ${d} d`}</Badge>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ContarZona({ almacenId, zonaId }: { almacenId: string; zonaId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { esAdmin, perfil } = useAuth();
  const [, navegar] = useLocation();
  const modoErp = perfil?.empresa.modo_ventas === "erp";
  const [verExistencias, setVerExistencias] = useState(false);
  const [filas, setFilas] = useState<Record<string, EstadoFila>>({});
  const [agregar, setAgregar] = useState(false);
  const [extra, setExtra] = useState<Existencia[]>([]);
  const [terminado, setTerminado] = useState(false);
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});
  const enVuelo = useRef<Set<string>>(new Set());
  const guardados = useRef<Record<string, number>>({});
  const pendiente = useRef<Set<string>>(new Set()); // tecleó algo nuevo mientras había un guardado en vuelo

  const zona = useQuery({
    queryKey: ["zonas", zonaId],
    queryFn: async () => {
      const { data, error } = await supabase.from("zonas").select("*").eq("id", zonaId).single();
      if (error) throw error;
      return data as Zona;
    },
  });

  // Sin refetch al volver a la pestaña: no pisar celdas tecleadas.
  const lista = useQuery({
    queryKey: ["existencias", "zona", zonaId],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await supabase.from("v_existencias").select("*").eq("almacen_id", almacenId).eq("zona_id", zonaId).eq("activo", true).order("ubicacion", { nullsFirst: false }).order("sku");
      if (error) throw error;
      return data;
    },
  });

  const esSinZona = zona.data?.codigo === "SIN-ZONA";
  const modoBusqueda = esSinZona || (lista.data?.length ?? 0) > LIMITE_LISTA;
  const items = useMemo(() => {
    const base = modoBusqueda ? [] : (lista.data ?? []);
    const ids = new Set(base.map((e) => e.id));
    return [...base, ...extra.filter((e) => !ids.has(e.id))];
  }, [lista.data, extra, modoBusqueda]);

  const guardar = useCallback(async (e: Existencia) => {
    const f = filas[e.id!];
    const texto = (f?.valor ?? "").replace(",", ".").trim();
    if (texto === "") return;                       // celda vacía = no contado
    const n = Number(texto);
    if (!Number.isFinite(n) || n < 0) {
      setFilas((s) => ({ ...s, [e.id!]: { ...s[e.id!], estado: "error", error: "Número inválido" } }));
      return;
    }
    // Guardas SÍNCRONAS (el estado de React llega tarde): sin doble guardado por Enter + blur,
    // y sin re-guardar el mismo valor.
    if (enVuelo.current.has(e.id!)) { pendiente.current.add(e.id!); return; }
    if (guardados.current[e.id!] === n) return;
    enVuelo.current.add(e.id!);
    setFilas((s) => ({ ...s, [e.id!]: { ...s[e.id!], valor: texto, estado: "guardando" } }));
    try {
      const c = await rpc("registrar_conteo", { p_producto_id: e.producto_id!, p_almacen_id: almacenId, p_zona_id: zonaId, p_cantidad_fisica: n });
      guardados.current[e.id!] = n;
      setFilas((s) => {
        const previo = s[e.id!];
        // conserva el "anterior" del primer conteo de la sesión para el resumen
        const anterior = previo?.anterior ?? Number(c.cantidad_anterior);
        // si el usuario siguió tecleando mientras guardábamos, NO pisar su valor: queda idle y se re-guarda
        const cambio = !!previo && previo.valor.replace(",", ".").trim() !== texto;
        return { ...s, [e.id!]: { valor: cambio ? previo.valor : texto, estado: cambio ? "idle" : "ok", anterior, nuevo: Number(c.cantidad_fisica) } };
      });
    } catch (err) {
      const ea = mapearErrorSupabase(err);
      registrarError("registrar_conteo", ea, { existencia: e.id });
      setFilas((s) => ({ ...s, [e.id!]: { ...s[e.id!], estado: "error", error: ea.mensaje } }));
    } finally {
      enVuelo.current.delete(e.id!);
    }
  }, [filas, almacenId, zonaId]);

  // Re-guardar las filas que cambiaron mientras había un guardado en vuelo.
  useEffect(() => {
    for (const id of [...pendiente.current]) {
      if (enVuelo.current.has(id)) continue;
      pendiente.current.delete(id);
      const e = items.find((x) => x.id === id);
      if (e && filas[id]?.estado === "idle") void guardar(e);
    }
  }, [filas, items, guardar]);

  // Guardar lo pendiente si el usuario cambia de app (visibilitychange) o cierra.
  useEffect(() => {
    const h = () => {
      if (document.visibilityState !== "hidden") return;
      for (const e of items) {
        const f = filas[e.id!];
        if (f && f.estado === "idle" && f.valor.trim() !== "") void guardar(e);
      }
    };
    document.addEventListener("visibilitychange", h);
    return () => document.removeEventListener("visibilitychange", h);
  }, [items, filas, guardar]);

  const contados = items.filter((e) => filas[e.id!]?.estado === "ok").length;
  const sinGuardar = items.filter((e) => filas[e.id!]?.estado === "error").length;

  async function terminar() {
    // Guardar cualquier celda tecleada que quedó sin guardar
    for (const e of items) {
      const f = filas[e.id!];
      if (f && f.estado === "idle" && f.valor.trim() !== "") await guardar(e);
    }
    await Promise.all(CLAVES_DOMINIO.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    setTerminado(true);
  }

  if (zona.isLoading || lista.isLoading) return <Esqueleto filas={8} />;
  if (lista.error) return <ErrorBloque mensaje={mapearErrorSupabase(lista.error).mensaje} reintentar={() => lista.refetch()} />;

  if (terminado) {
    const ok = items.filter((e) => filas[e.id!]?.estado === "ok").map((e) => ({ e, f: filas[e.id!] }));
    const difs = ok.filter(({ f }) => (f.nuevo ?? 0) !== (f.anterior ?? 0)).sort((a, b) => Math.abs((b.f.nuevo ?? 0) - (b.f.anterior ?? 0)) - Math.abs((a.f.nuevo ?? 0) - (a.f.anterior ?? 0)));
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Tarjeta>
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-emerald-100 p-2 text-emerald-700"><Check className="h-6 w-6" /></div>
            <div>
              <h1 className="text-xl font-bold">Listo. Tu conteo quedó registrado.</h1>
              <p className="text-tinta-suave">Zona {zona.data?.codigo} · {ok.length} SKUs contados por ti a las {new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false })}. Esto te cubre: quedó quién contó y cuándo.</p>
            </div>
          </div>
          {difs.length > 0 && (
            <div className="mt-4">
              <h2 className="mb-2 font-semibold">Lo que encontraste distinto</h2>
              <ul className="divide-y divide-borde text-sm">
                {difs.slice(0, 10).map(({ e, f }) => (
                  <li key={e.id} className="flex justify-between gap-2 py-2">
                    <span><span className="font-medium">{e.sku}</span> <span className="text-tinta-suave">{e.nombre}</span></span>
                    <span className="tabular">el sistema decía {num(f.anterior)}, encontraste <strong>{num(f.nuevo)}</strong> ({signo((f.nuevo ?? 0) - (f.anterior ?? 0))}) · se ajustó</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {difs.length === 0 && ok.length > 0 && <p className="mt-3 text-emerald-800">Todo cuadró: ninguna diferencia.</p>}
          {sinGuardar > 0 && <p className="mt-3 text-red-700">{sinGuardar} fila(s) no se guardaron. Regresa y reintenta.</p>}
          <div className="mt-5 flex flex-wrap gap-2">
            <Link href="/contar"><Boton>Contar otra zona</Boton></Link>
            {esAdmin && <Link href="/hoy"><Boton variante="secundario">Ir a Hoy</Boton></Link>}
            {sinGuardar > 0 && <Boton variante="secundario" onClick={() => setTerminado(false)}>Regresar</Boton>}
          </div>
        </Tarjeta>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Link href="/contar" className="rounded p-1 hover:bg-gray-100" aria-label="Volver a zonas"><ArrowLeft className="h-5 w-5" /></Link>
          <h1 className="text-2xl font-bold">Zona {zona.data?.codigo} <span className="font-normal text-tinta-suave">{zona.data?.nombre}</span></h1>
        </div>
        {esAdmin && (
          <button onClick={() => setVerExistencias((v) => !v)} className="flex items-center gap-1 text-sm text-tinta-suave hover:text-tinta">
            {verExistencias ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {verExistencias ? "Ocultar existencias" : "Mostrar existencias"}
          </button>
        )}
      </div>

      {modoBusqueda && (
        <Tarjeta>
          <p className="mb-3 text-sm text-tinta-suave">{esSinZona ? "Estos productos no tienen zona: cuéntalos buscando por clave." : `Esta zona tiene más de ${LIMITE_LISTA} SKUs: cuéntalos buscando por clave.`}</p>
          <BuscadorSku almacenId={almacenId} mostrarFisico={verExistencias} onElegir={(e) => { setExtra((x) => (x.some((y) => y.id === e.id) ? x : [e, ...x])); window.setTimeout(() => inputs.current[e.id!]?.focus(), 50); }} placeholder="Busca el SKU y teclea cuántos hay" />
        </Tarjeta>
      )}

      {items.length === 0 && !modoBusqueda ? (
        <Tarjeta><Vacio titulo="Esta zona no tiene SKUs" texto="Agrega el producto que tengas enfrente." accion={<Boton onClick={() => setAgregar(true)}><Plus className="h-4 w-4" /> Agregar SKU a este conteo</Boton>} /></Tarjeta>
      ) : (
        <ul className="divide-y divide-borde rounded-md border border-borde bg-white">
          {items.map((e, i) => {
            const f = filas[e.id!];
            return (
              <li key={e.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{e.sku} <span className="font-normal text-tinta-suave">{e.nombre}</span></div>
                  <div className="text-xs text-tinta-suave">
                    {e.ubicacion ? `${e.ubicacion} · ` : ""}{e.unidad}
                    {verExistencias && <> · sistema: {num(e.cantidad_fisica)}{modoErp && e.existencia_erp != null ? ` · tu sistema: ${num(e.existencia_erp)}` : ""}</>}
                    {e.ultimo_conteo_en && <> · contado {hace(e.ultimo_conteo_en)} por {e.ultimo_conteo_por_nombre ?? "alguien"}</>}
                  </div>
                  {f?.estado === "error" && <div className="flex items-center gap-1 text-xs text-red-700"><AlertCircle className="h-3.5 w-3.5" /> {f.error} <button className="underline" onClick={() => guardar(e)}>Reintentar</button></div>}
                </div>
                <div className="relative w-28 shrink-0">
                  <Input
                    ref={(el) => { inputs.current[e.id!] = el; }}
                    inputMode="decimal"
                    enterKeyHint="next"
                    aria-label={`Cantidad física de ${e.sku}`}
                    placeholder="0"
                    value={f?.valor ?? ""}
                    onChange={(ev) => setFilas((s) => ({ ...s, [e.id!]: { valor: ev.target.value, estado: "idle" } }))}
                    onBlur={() => guardar(e)}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter") {
                        ev.preventDefault();
                        void guardar(e);
                        const sig = items[i + 1];
                        if (sig) inputs.current[sig.id!]?.focus();
                      }
                    }}
                    className={`tabular h-14 text-center text-2xl font-semibold ${f?.estado === "ok" ? "border-emerald-500 pr-8" : f?.estado === "error" ? "border-error" : ""}`}
                  />
                  {f?.estado === "ok" && <Check className="pointer-events-none absolute right-2 top-4 h-5 w-5 text-emerald-600" aria-label="Guardado" />}
                  {f?.estado === "guardando" && <span className="absolute right-2 top-4 h-5 w-5 animate-pulse rounded-full bg-gray-300" aria-label="Guardando" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!modoBusqueda && items.length > 0 && (
        <button onClick={() => setAgregar(true)} className="flex items-center gap-1 text-sm text-primary underline"><Plus className="h-4 w-4" /> Agregar SKU a este conteo</button>
      )}

      {/* Barra sticky */}
      <div className="sticky bottom-20 z-20 flex items-center justify-between gap-3 rounded-md border border-borde bg-white/95 p-3 shadow-lg backdrop-blur md:bottom-4">
        <div className="tabular text-sm"><strong>{contados}</strong>/{items.length} contados{sinGuardar > 0 && <span className="text-red-700"> · {sinGuardar} sin guardar</span>}</div>
        <Boton onClick={terminar} disabled={contados === 0 && sinGuardar === 0}>Terminar zona</Boton>
      </div>

      <Dialogo abierto={agregar} onCerrar={() => setAgregar(false)} titulo="Agregar SKU a este conteo">
        <p className="mb-3 text-sm text-tinta-suave">Busca el producto que tienes enfrente. Al contarlo, se moverá a esta zona.</p>
        <BuscadorSku almacenId={almacenId} mostrarFisico={verExistencias} onElegir={(e) => {
          setExtra((x) => (x.some((y) => y.id === e.id) ? x : [...x, e]));
          setAgregar(false);
          toast.info(`${e.sku} agregado. Teclea la cantidad.`);
          window.setTimeout(() => inputs.current[e.id!]?.focus(), 100);
        }} />
      </Dialogo>
      <span className="hidden">{cantidad(0)}{navegar.name}</span>
    </div>
  );
}
